import { afterNextRender, afterRenderEffect, Component, computed, DestroyRef, ElementRef, inject, input, signal, viewChild, ViewChild } from '@angular/core';
import { LayoutService } from '@/layout/service/layout.service';
import { ChartModule, UIChart } from 'primeng/chart';
import moment from 'moment';
import 'chartjs-adapter-moment';
import { SystemMonitorStat } from 'src/api/system-monitor-stat';
import { ChartData, ChartOptions, ScatterDataPoint } from 'chart.js';
import { ChartPalette, chartColor, readChartPalette } from './chart-theme';
import { TranslationService } from '@/layout/service/translation.service';

@Component({
    selector: 'app-system-monitor-chart',
    standalone: true,
    imports: [ChartModule],
    template: `
        <span #paletteProbe class="palette-probe" aria-hidden="true"></span>
        <div class="chart-frame">
            @if (palette()) {
                <p-chart #chart type="line" [ariaLabel]="t()('dashboard.systemMonitorChart')" [data]="chartData()" [options]="chartOptions()"></p-chart>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
                flex: 1;
                min-width: 0;
                width: 100%;
            }
            .palette-probe {
                display: none;
                transition: none !important;
            }
            .chart-frame {
                position: relative;
                width: 100%;
                height: clamp(15rem, 30vw, 22rem);
            }
            p-chart {
                display: block;
                position: relative;
                width: 100%;
                height: 100%;
            }
        `
    ]
})
export class SystemMonitorChartComponent {
    @ViewChild('chart') chartComponent?: UIChart;
    private layoutService = inject(LayoutService);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;
    private paletteProbe = viewChild.required<ElementRef<HTMLElement>>('paletteProbe');
    readonly palette = signal<ChartPalette | null>(null, { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) });

    constructor() {
        afterRenderEffect(() => {
            this.layoutService.layoutConfig();
            this.layoutService.transitionComplete();
            this.refreshPalette();
        });
        afterNextRender(() => {
            // View-transition callbacks can apply the DOM theme after the config signal
            // changes. Observe the applied class as well, including system-theme changes.
            const observer = new MutationObserver(() => this.refreshPalette());
            observer.observe(this.paletteProbe().nativeElement.ownerDocument.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });
            this.destroyRef.onDestroy(() => observer.disconnect());
        });
    }

    private refreshPalette(): void {
        this.palette.set(readChartPalette(this.paletteProbe().nativeElement));
    }

    // Full history provided by parent (already windowed by selected duration)
    stats = input<SystemMonitorStat[]>([]);
    // Selected window hours (1, 24, 168) for axis granularity
    windowHours = input<number>(1);

    private filteredWindow = computed(() => {
        const history = this.stats();
        if (!history || history.length === 0) return [] as SystemMonitorStat[];
        return [...history].sort((a, b) => a.timestampUtc.localeCompare(b.timestampUtc));
    });

    chartData = computed<ChartData<'line', ScatterDataPoint[]>>(() => {
        const palette = this.palette();
        const windowPoints = this.filteredWindow();
        if (!palette || windowPoints.length === 0) return { datasets: [] };
        const cpuPoints = windowPoints.map((h) => ({ x: moment.utc(h.timestampUtc).valueOf(), y: Math.round(h.cpu) }));
        const memPoints = windowPoints.map((h) => ({ x: moment.utc(h.timestampUtc).valueOf(), y: !h.memoryTotal ? 0 : Math.min(100, Math.round((h.memoryUsed / h.memoryTotal) * 100)) }));
        const procMemPoints = windowPoints.map((h) => ({ x: moment.utc(h.timestampUtc).valueOf(), y: !h.memoryProcessTotal ? 0 : Math.min(100, Math.round((h.memoryProcessUsed / h.memoryProcessTotal) * 100)) }));
        const procMemAbsPoints = windowPoints.map((h) => ({ x: moment.utc(h.timestampUtc).valueOf(), y: Math.round((h.memoryProcessTotal || 0) / (1024 * 1024)) }));
        const primary = chartColor(palette.cpu);
        const secondaryColor = chartColor(palette.memory);
        const infoColor = chartColor(palette.process);
        const cpuFill = chartColor(palette.cpu, 0.1);
        const memFill = chartColor(palette.memory, 0.1);
        const procFill = chartColor(palette.process, 0.1);
        return {
            datasets: [
                { label: 'CPU %', data: cpuPoints, borderColor: primary, backgroundColor: cpuFill, fill: true, tension: 0.25, pointRadius: 0, pointHoverRadius: 0, hitRadius: 4, yAxisID: 'y1' },
                { label: 'System Memory %', data: memPoints, borderColor: secondaryColor, backgroundColor: memFill, fill: true, tension: 0.25, pointRadius: 0, pointHoverRadius: 0, hitRadius: 4, yAxisID: 'y1' },
                { label: 'Process Memory %', data: procMemPoints, borderColor: infoColor, backgroundColor: procFill, fill: true, tension: 0.25, pointRadius: 0, pointHoverRadius: 0, hitRadius: 4, yAxisID: 'y1' },
                { label: 'Allocated Memory (MB)', data: procMemAbsPoints, borderColor: infoColor, backgroundColor: 'transparent', fill: false, tension: 0.25, pointRadius: 0, pointHoverRadius: 0, hitRadius: 4, borderDash: [6, 3], yAxisID: 'y2' }
            ]
        };
    });

    chartOptions = computed<ChartOptions<'line'>>(() => {
        const palette = this.palette();
        if (!palette) return {};
        const full = this.stats();
        const hrs = this.windowHours();
        // Set x-axis range based on actual data range, not requested window
        // This ensures the chart scales to show the data that's actually available
        let min: number | undefined;
        let max: number | undefined;
        if (full.length) {
            const sorted = [...full].sort((a, b) => a.timestampUtc.localeCompare(b.timestampUtc));
            const earliest = moment.utc(sorted[0].timestampUtc).valueOf();
            const last = moment.utc(sorted[sorted.length - 1].timestampUtc).valueOf();
            min = earliest;
            max = last;
        }

        // Compute abs proc mem max for y2 scale
        let procAbsMax = 0;
        const windowPoints = this.filteredWindow();
        for (const h of windowPoints) {
            const mb = (h.memoryProcessTotal || 0) / (1024 * 1024);
            if (mb > procAbsMax) procAbsMax = mb;
        }
        if (procAbsMax > 0) {
            const magnitude = Math.pow(10, Math.floor(Math.log10(procAbsMax)));
            const step = magnitude / 2;
            procAbsMax = Math.ceil(procAbsMax / step) * step;
        }

        const textColor = chartColor(palette.text);
        const textColorSecondary = chartColor(palette.muted);
        const borderColor = chartColor(palette.border);

        // Determine appropriate x-axis granularity for long term vs short term
        const timeUnit = hrs <= 1 ? 'minute' : hrs <= 24 ? 'hour' : 'day';
        const displayFormats = hrs <= 1 ? { minute: 'HH:mm' } : hrs <= 24 ? { hour: 'MMM D HH:mm' } : { day: 'MMM D' };
        const tooltipFormat = hrs <= 1 ? 'HH:mm:ss' : hrs <= 24 ? 'MMM D HH:mm:ss' : 'MMM D HH:mm';

        return {
            maintainAspectRatio: false,
            responsive: true,
            animation: false,
            plugins: {
                legend: { labels: { color: textColor, usePointStyle: true, pointStyle: 'line', padding: 18 } },
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: chartColor(palette.surface),
                    titleColor: textColor,
                    bodyColor: textColor,
                    borderColor,
                    borderWidth: 1
                }
            },
            interaction: { mode: 'nearest', intersect: false },
            scales: {
                x: {
                    type: 'time',
                    min,
                    max,
                    time: { unit: timeUnit, displayFormats, tooltipFormat },
                    ticks: {
                        color: textColorSecondary,
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: hrs <= 1 ? 6 : hrs <= 24 ? 10 : 8,
                        autoSkipPadding: 12
                    },
                    grid: { display: false },
                    border: { color: borderColor }
                },
                y1: {
                    type: 'linear',
                    position: 'left',
                    min: 0,
                    max: 100,
                    ticks: {
                        color: textColorSecondary,
                        callback: (v) => v + '%',
                        maxTicksLimit: 6,
                        stepSize: 20
                    },
                    grid: { display: false },
                    border: { color: borderColor }
                },
                y2: {
                    type: 'linear',
                    position: 'right',
                    min: 0,
                    suggestedMax: procAbsMax || 10,
                    ticks: {
                        color: textColorSecondary,
                        maxTicksLimit: 6,
                        callback: (v) => (Number(v) >= 1024 ? (Number(v) / 1024).toFixed(1) + ' GB' : v + ' MB')
                    },
                    grid: { display: false },
                    border: { color: borderColor }
                }
            }
        };
    });
}
